import React from 'react';
import { Link as AriaLink } from 'react-aria-components';

import { tv } from '../../tv';
import { PressEvent } from '../../types';

/**
 * ComplyKit Link: the text actionable. Where a Button commands, a Link
 * navigates or acts inline in text: the entity reference in an activity
 * line, the "view report" beside a check, the sentence-embedded action a
 * translation string carries.
 *
 * - Links are words: `children` is the text, and there are deliberately no
 *   icon slots. An icon-only action is IconButton, never a bare glyph
 *   wearing a link.
 * - The union is Button's: somewhere to go (`href`, `target`, `rel`) or
 *   something to do (`onPress`), never both. There is no `isPending` (a
 *   text run has no box to reserve a spinner in; work that needs in-flight
 *   feedback is a Button's job) and no `type` (a Link never submits a form).
 * - `size` defaults to `inherit` because a link rides the sentence it sits
 *   in; `sm`/`md` exist for the standalone seats (a load-more, a toggle
 *   under a table) that have no surrounding text to ride.
 * - Underline: none at rest, underline on hover, the system focus ring on
 *   keyboard focus. The colour axis is closed at `accent`, `muted` and
 *   `inherit`; `inherit` rides the surrounding colour and keeps its
 *   underline at rest, because on a colored surface the underline is the
 *   only affordance left.
 */
interface LinkCommonProps {
  /** The link's text. Links are words: no icon slots, and an icon-only
   * action is IconButton, not this. */
  children: React.ReactNode;
  /**
   * Text size. `inherit` (the default) rides the surrounding text, which is
   * what an in-prose link wants; `sm`/`md` are for standalone seats with no
   * sentence to ride, and carry the medium weight the other controls use.
   */
  size?: 'inherit' | 'sm' | 'md';
  /**
   * `accent` is the brand accent text colour, the reading "this is a link";
   * `muted` is for a secondary in-prose action that should not outshine the
   * copy around it. Both underline on hover. `inherit` takes the surrounding
   * text colour entirely and underlines at rest: on a tinted or inverted
   * surface colour cannot mark the link, so the underline does.
   */
  color?: 'accent' | 'muted' | 'inherit';
  /**
   * Whether the link is disabled. A disabled navigation drops its anchor
   * entirely (React Aria's Link: nothing to press, copy, or reach by
   * keyboard); a disabled action keeps `role="link"` with `aria-disabled`
   * and every press suppressed.
   */
  isDisabled?: boolean;
}

/* Somewhere to go OR something to do, the same union Button holds: a
 * navigation cannot carry an in-page action and an action cannot carry a
 * target, so the invalid combinations fail to compile instead of being
 * silently ignored at runtime. */
interface LinkNavigationProps extends LinkCommonProps {
  /** Where the link goes. Renders a real anchor, routed through the
   * RouterProvider bridge at the app root, so same-app clicks
   * client-navigate and cmd-click / copy-address still work natively. */
  href: string;
  /** Link target, for the rare link that opens away from the app. */
  target?: '_blank';
  /** Link relationship. When `target="_blank"` and no `rel` is given,
   * `noopener noreferrer` is applied automatically. */
  rel?: string;
  onPress?: never;
}

interface LinkActionProps extends LinkCommonProps {
  /**
   * What the link does: an in-page action that reads as text. Presses do
   * not bubble, and the event carries the modifier keys held during the
   * press (see `PressEvent`).
   */
  onPress: (e: PressEvent) => void;
  href?: never;
  target?: never;
  rel?: never;
}

export type LinkProps = LinkNavigationProps | LinkActionProps;

const linkStyles = tv({
  base: [
    // Inline text, never a box: it wraps with the sentence it sits in.
    'ck:cursor-pointer',
    'ck:no-underline ck:hover:underline',
    'ck:focus-visible:focus-ring',
    'ck:disabled:opacity-50 ck:disabled:cursor-not-allowed',
  ],
  variants: {
    /* sm/md carry the medium weight because a standalone link is a control
     * and reads at control weight; inherit carries nothing, so an in-prose
     * link takes the sentence's own size and weight. */
    size: {
      inherit: '',
      sm: 'ck:text-sm ck:font-medium',
      md: 'ck:text-base ck:font-medium',
    },
    color: {
      accent: 'ck:text-accent-11',
      muted: 'ck:text-text-muted',
      // rest underline: on a colored surface it is the only link affordance
      inherit: 'ck:text-inherit ck:underline',
    },
  },
  /* No defaultVariants: every axis is passed explicitly by the component,
   * the same camp as Button, so there is no second place a default could
   * drift. */
});

export const Link = React.forwardRef<HTMLElement, LinkProps>(function Link(
  { children, size = 'inherit', color = 'accent', isDisabled = false, onPress, href, target, rel },
  ref,
) {
  /* One render path: React Aria's Link infers the element itself, an anchor
   * for an enabled href and a span with role="link" (focusable, pressable)
   * for the action form, so element, keyboard behaviour and focus ring
   * travel together exactly as Button's do.
   *
   * The navigation props are spread conditionally, never passed as
   * undefined: React Aria copies `href` onto the element whenever the KEY
   * is present, so an explicit href={undefined} would leave a bogus href=""
   * on the action form's span. */
  const formProps = href
    ? { href, target, rel: rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined) }
    : { onPress };

  return (
    <AriaLink
      {...formProps}
      // The element is an anchor or a span by RAC's inference; the cast is
      // confined here rather than pushed onto callers.
      ref={ref as React.Ref<HTMLAnchorElement>}
      isDisabled={isDisabled}
      className={linkStyles({ size, color })}
      data-component="link"
    >
      {children}
    </AriaLink>
  );
});
